import { router } from '@inertiajs/core'
import { onSignedIn, onSignedOut } from '@/lib/appEvents'
import { promiseWithResolvers } from '@/lib/promise'
import { pageConfigs } from './index'

type VisitHref = Parameters<typeof router.visit>[0]
type VisitOptions = NonNullable<Parameters<typeof router.visit>[1]>
type PrefetchOptions = NonNullable<Parameters<typeof router.prefetch>[2]> & {
  fixVisitToTheSameUrl?: boolean
}

onSignedIn('inertiaPrefetch', () => router.flushAll())
onSignedOut('inertiaPrefetch', () => router.flushAll())

// #region Visit with cache

export async function visitWithCache(
  href: VisitHref,
  options?: VisitOptions,
  prefetchOptionsArg?: PrefetchOptions,
): Promise<void>
export async function visitWithCache(
  href: VisitHref,
  visitAndPrefetchVisitOptions?: {
    visit?: VisitOptions
    prefetch?: VisitOptions
  },
  prefetchOptionsArg?: PrefetchOptions,
): Promise<void>

export async function visitWithCache(
  href: VisitHref,
  visitAndPrefetchVisitOptions?:
    | VisitOptions
    | {
        visit?: VisitOptions
        prefetch?: VisitOptions
      },
  prefetchOptions?: PrefetchOptions,
): Promise<void> {
  const { visitOptions, prefetchVisitOptions } = getVisitAndPrefetchVisitOptions(
    visitAndPrefetchVisitOptions,
  )

  await fixVisitToTheSameUrl(href, visitOptions)
  prefetchWithCache(href, prefetchVisitOptions, { ...prefetchOptions, fixVisitToTheSameUrl: false })

  await wait(1)

  const { promise, wrapVisitOptions } = promiseVisitOptions()
  router.visit(href, wrapVisitOptions(visitOptions))

  return await promise
}

// #endregion
// #region Prefetch with cache

export const prefetchWithCache = async (
  href: VisitHref,
  options?: VisitOptions,
  prefetchOptionsArg?: PrefetchOptions,
) => {
  const url = visitUrlToString(href).slice(window.location.origin.length)
  const pageConfig = pageConfigs.getPageConfig({ url })
  const prefetchOptions = {
    ...pageConfig?.call(state => pageConfig?.cacheFor?.({ url }, state)),
    ...prefetchOptionsArg,
  }

  if (prefetchOptions?.fixVisitToTheSameUrl ?? true) {
    await fixVisitToTheSameUrl(href, options)
  }

  const { promise, resolve, wrapVisitOptions } = promiseVisitOptions()
  router.prefetch(
    href,
    wrapVisitOptions({
      ...options,
      onBefore: visit => {
        fixPurposePrefetch(visit)

        if (router.getCached(href, options)) {
          resolve()
        }

        return options?.onBefore?.(visit)
      },
      onPrefetched: (response, visit) => {
        if (hasRedirected(response) || !(response.status >= 200 && response.status < 300)) {
          setTimeout(() => router.flush(href, options), 1)
        }

        return options?.onPrefetched?.(response, visit)
      },
    }),
    prefetchOptions,
  )

  return await promise
}

// #endregion
// #region Helpers

const promiseVisitOptions = () => {
  const promise = promiseWithResolvers<void>()

  return {
    ...promise,
    wrapVisitOptions: (visitOptions: VisitOptions | undefined): VisitOptions => ({
      ...visitOptions,
      onCancel: () => {
        visitOptions?.onCancel?.()
        promise.reject('Cancelled')
      },
      onFinish: event => {
        visitOptions?.onFinish?.(event)
        promise.resolve()
      },
    }),
  }
}

const isTheSameUrl = (visitUrl: VisitHref, otherUrl: VisitHref = new URL(window.location.href)) => {
  const url = visitUrlToUrl(visitUrl)
  const other = visitUrlToUrl(otherUrl)

  const fullUrl = url.origin + url.pathname + url.search
  const currentUrl = other.origin + other.pathname + other.search

  return fullUrl === currentUrl
}

const visitUrlToUrl = (href: VisitHref): URL => {
  if (href instanceof URL) {
    return href
  } else if (typeof href === 'string') {
    return new URL(href, window.location.origin)
  } else {
    return new URL(href.url, window.location.origin)
  }
}

const visitUrlToString = (href: VisitHref) => {
  if (typeof href === 'string') {
    return href
  } else if (href instanceof URL) {
    return href.href
  } else {
    return href.url.toString()
  }
}

const hasRedirected = (
  response: Parameters<NonNullable<NonNullable<VisitOptions>['onPrefetched']>>[0],
) => response?.config.url !== response?.request?.responseURL

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

const getVisitAndPrefetchVisitOptions = (
  visitAndPrefetchVisitOptions?: VisitOptions | { visit?: VisitOptions; prefetch?: VisitOptions },
) => {
  if (
    (visitAndPrefetchVisitOptions as any)?.visit ||
    typeof (visitAndPrefetchVisitOptions as any)?.prefetch === 'object'
  ) {
    return {
      visitOptions: (visitAndPrefetchVisitOptions as any).visit,
      prefetchVisitOptions: (visitAndPrefetchVisitOptions as any).prefetch,
    }
  }

  return {
    visitOptions: visitAndPrefetchVisitOptions as VisitOptions | undefined,
    prefetchVisitOptions: undefined,
  }
}

const fixPurposePrefetch = (
  visit: Parameters<NonNullable<NonNullable<VisitOptions>['onBefore']>>[0],
) => {
  if (
    visit.prefetch &&
    visit.headers?.Purpose === 'prefetch' &&
    (visit.data as any)?.Purpose === 'prefetch'
  ) {
    delete (visit.data as any).Purpose
  }
}

const fixVisitToTheSameUrl = async (href: VisitHref, options?: VisitOptions) => {
  if (router.getCached(href, options) || !isTheSameUrl(href)) {
    return
  }

  router.replace({
    url: (() => {
      const href = new URL(window.location.href)
      href.searchParams.append('_c', '')
      return href.toString().at(-1) === '=' ? href.toString().slice(0, -1) : href.toString()
    })(),
  })

  for (let i = 0; i < 10; i++) {
    await wait(50)

    if (!isTheSameUrl(href)) {
      return
    }
  }
}
